import React from "react";

interface PaginationProps {
  currentPage: number;
  onPageChange: (page: number) => void;
  pageRangeDisplayed: number;
  isLoading: boolean;
  hasNextPage: boolean;
}

const PrevArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={3}
    stroke="currentColor"
    className="h-4 w-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 19.5 8.25 12l7.5-7.5"
    />
  </svg>
);

const NextArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={3}
    stroke="currentColor"
    className="h-4 w-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.25 4.5l7.5 7.5-7.5 7.5"
    />
  </svg>
);

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  onPageChange,
  pageRangeDisplayed,
  isLoading,
  hasNextPage,
}) => {
  const safeCurrentPage = Math.max(1, currentPage);
  const startPage = Math.max(1, safeCurrentPage - pageRangeDisplayed);

  let endPage;
  if (!hasNextPage) {
    endPage = safeCurrentPage;
  } else {
    endPage = safeCurrentPage + pageRangeDisplayed;
  }

  const pages: number[] = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const handlePageClick = (
    page: number,
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault();
    if (!isLoading && page >= 1 && page !== safeCurrentPage) {
      onPageChange(page);
    }
  };

  const getPageHref = (page: number): string => {
    const params = new URLSearchParams(window.location.search);
    const pageInHref = Math.max(1, page);
    params.set("page", pageInHref.toString());
    return `?${params.toString()}`;
  };

  const baseButtonClasses =
    "w-10 h-10 flex items-center justify-center text-sm font-medium rounded-full";

  const enabledNonActiveClasses =
    "bg-white text-gray-700 border border-transparent hover:bg-soluud-primary-light hover:text-soluud-primary transition-colors duration-150";
  const activeClasses =
    "bg-soluud-primary text-white border border-soluud-primary";
  const disabledClasses =
    "bg-gray-200 text-gray-500 cursor-not-allowed pointer-events-none opacity-60";

  const showTrailingEllipsis = hasNextPage && endPage > safeCurrentPage;

  return (
    <nav
      className="mt-4 flex items-center justify-center space-x-2"
      aria-label="Pagination"
    >
      <a
        href={getPageHref(safeCurrentPage - 1)}
        onClick={(e) => handlePageClick(safeCurrentPage - 1, e)}
        className={`${baseButtonClasses} ${
          isLoading || safeCurrentPage <= 1
            ? disabledClasses
            : enabledNonActiveClasses
        }`}
        aria-label="Previous Page"
        aria-disabled={isLoading || safeCurrentPage <= 1}
        tabIndex={isLoading || safeCurrentPage <= 1 ? -1 : 0}
      >
        <PrevArrowIcon />
      </a>

      {startPage > 1 && (
        <>
          <a
            href={getPageHref(1)}
            onClick={(e) => handlePageClick(1, e)}
            className={`${baseButtonClasses} ${
              isLoading ? disabledClasses : enabledNonActiveClasses
            }`}
            aria-disabled={isLoading}
            tabIndex={isLoading ? -1 : 0}
          >
            1
          </a>
          {startPage > 2 && (
            <span className="flex h-10 w-10 items-center justify-center text-sm font-medium text-gray-700">
              ...
            </span>
          )}
        </>
      )}

      {pages.map((page) => (
        <a
          key={page}
          href={getPageHref(page)}
          onClick={(e) => handlePageClick(page, e)}
          className={`${baseButtonClasses} ${
            isLoading
              ? disabledClasses
              : safeCurrentPage === page
                ? activeClasses
                : enabledNonActiveClasses
          }`}
          aria-current={
            safeCurrentPage === page && !isLoading ? "page" : undefined
          }
          aria-disabled={isLoading}
          tabIndex={isLoading ? -1 : 0}
        >
          {page}
        </a>
      ))}

      {showTrailingEllipsis && (
        <span className="flex h-10 w-10 items-center justify-center text-sm font-medium text-gray-700">
          ...
        </span>
      )}

      <a
        href={getPageHref(safeCurrentPage + 1)}
        onClick={(e) => handlePageClick(safeCurrentPage + 1, e)}
        className={`${baseButtonClasses} ${
          isLoading || !hasNextPage ? disabledClasses : enabledNonActiveClasses
        }`}
        aria-label="Next Page"
        aria-disabled={isLoading || !hasNextPage}
        tabIndex={isLoading || !hasNextPage ? -1 : 0}
      >
        <NextArrowIcon />
      </a>
    </nav>
  );
};

export default Pagination;
